/**
 * POC animated lobby — one-shot class helper.
 *
 * Adds a class, removes it on the element's own `animationend` (bubbled children are ignored) with a
 * timeout fallback, so a hook class is never left behind: no permanent animation, no permanent layer.
 */

import { FX_ENABLED } from './fxFlag';
import { getMotionTier } from './motionTier';

export const playOneShotClass = (element: Element | null, className: string, fallbackMs = 1500): void => {
  if (!element || !FX_ENABLED || getMotionTier() === 'off') return;

  // Restart when the same one-shot is still running (rapid taps): drop it, reflow, add again.
  if (element.classList.contains(className)) {
    element.classList.remove(className);
    void (element as HTMLElement).offsetWidth;
  }

  let timer: number | null = null;

  const done = (): void => {
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
    element.removeEventListener('animationend', onEnd);
    element.classList.remove(className);
  };

  const onEnd = (event: Event): void => {
    if (event.target === element) done();
  };

  element.addEventListener('animationend', onEnd);
  element.classList.add(className);
  timer = window.setTimeout(done, fallbackMs);
};

/**
 * Same contract for an attribute on <html>: the CSS hooks (card stagger, nav reveal) key off it and
 * it is removed after a fixed window, so cards mounting later animate with their stock fade only.
 */
export const stampHtmlAttribute = (attribute: string, durationMs: number): (() => void) => {
  const root = document.documentElement;
  root.setAttribute(attribute, '');
  const timer = window.setTimeout(() => root.removeAttribute(attribute), durationMs);
  return () => {
    window.clearTimeout(timer);
    root.removeAttribute(attribute);
  };
};
